/**
 * 任务对比页里的「行为对比」区块：当前任务对基线（对比）任务，在闭环段上逐场景比较「自车怎么开」。
 *
 * 本区块**只负责总览**：六张结论卡 + 占比条 + 强事件标签。场景明细不在这里，而是并入页面下方原有的
 * 「场景级对比明细」表（那里有「行为」列与「行为」筛选），这样只有一张明细表。
 *
 * 联动：点结论卡 / 强事件标签 = 设置原表的「行为结论」「强事件」筛选（由父级 JobTaskList 持有这两个状态，
 * 与翻转象限、「差异」列等取交集；区块本身不发明细请求）。点原表「行为」标签 = 打开本区块持有的详情抽屉。
 *
 * 权限：``menu:behavior-compare``。无权限时整个区块不渲染（调用方判断），后端同码兜底。
 * 该对任务还没有行为对比报告时显示明确提示，不显示空表。
 */
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Progress, Spin } from 'antd';
import { useEffect, useState } from 'react';

import {
  getBehaviorCompareSummary,
  getBehaviorProgress,
  getBehaviorCompareSceneDetail,
  getBehaviorReadiness,
  type BehaviorCompareSummary,
  type BehaviorProgress,
  type BehaviorReadiness,
  type BehaviorFilterValue,
  type BehaviorLabel,
  type BehaviorSceneRow,
} from '@/apis/simulation';

import OverviewCards from './components/OverviewCards';
import SceneDrawer from './components/SceneDrawer';
import './index.less';

interface Props {
  /** 当前任务 */
  jobId: number;
  /** 对比（基线）任务 */
  compareJobId: number;
  /** 外部（发起按钮）通知「进度变了」：自增即重新取汇总 */
  refreshKey?: number;
  /** 当前生效的「行为结论」筛选（由父级持有，与原表的「行为」下拉同一个状态） */
  activeLabel?: BehaviorFilterValue;
  /** 当前生效的「强事件」筛选 */
  activeStrong?: string;
  /** 点结论卡 / 强事件标签：一次改两个筛选（一次请求）。不带的项视为清除。 */
  onFilter: (next: { label?: BehaviorFilterValue; strong?: string }) => void;
  /** 要打开详情抽屉的场景（原表「行为」标签点击后由父级设置） */
  openSceneId?: number | null;
  onCloseScene: () => void;
}

/** 没有对比报告时的提示：按指纹就绪情况说清楚「差什么」，不只说一句没有。 */
function NoReportNotice({ readiness, reason }: { readiness: BehaviorReadiness | null; reason: string }) {
  if (!readiness) {
    return (
      <Alert type="info" showIcon message="这两个任务还没有行为对比报告" description={`${reason}。点筛选栏里的「发起行为对比」生成。`} />
    );
  }
  const { commonSceneNum, comparableNow, cur, base, backfillNeeded, state } = readiness;
  const pct = commonSceneNum > 0 ? Math.round((comparableNow / commonSceneNum) * 100) : 0;
  const side = (name: string, s: BehaviorReadiness['cur']) =>
    `${name}：指纹就绪 ${s.ready}/${s.total}${s.failed ? `，提取失败 ${s.failed}` : ''}${s.absent ? `，缺失 ${s.absent}` : ''}${s.unknown ? `，暂无法判定 ${s.unknown}` : ''}`;
  const detail = (
    <div style={{ fontSize: 12, lineHeight: 1.8 }}>
      <div>共同场景 {commonSceneNum.toLocaleString('en-US')} 个；两侧指纹都就绪 {comparableNow.toLocaleString('en-US')} 个。</div>
      <div>{side('当前任务', cur)}</div>
      <div>{side('对比任务', base)}</div>
      {commonSceneNum > 0 ? <Progress percent={pct} size="small" style={{ maxWidth: 360 }} /> : null}
    </div>
  );
  if (state === 'no_common') {
    return <Alert type="info" showIcon message="两个任务没有共同场景，无法做行为对比" />;
  }
  if (state === 'unknown') {
    return (
      <Alert type="warning" showIcon message="暂时无法确认指纹是否齐全（存储探测失败）"
        description={<>{detail}<div>稍后点「重新拉取」重试；这不代表缺失。</div></>} />
    );
  }
  if (state === 'ready') {
    return (
      <Alert type="success" showIcon message="指纹已齐全，可以直接对比"
        description={<>{detail}<div>对比报告尚未生成；生成后这里会显示结论总览与明细。</div></>} />
    );
  }
  return (
    <Alert
      type="info" showIcon
      message={state === 'partial' ? '部分场景的行为指纹已就绪' : '这两个任务还没有行为指纹'}
      description={<>
        {detail}
        <div>
          还需要补算 {backfillNeeded.toLocaleString('en-US')} 条指纹。点筛选栏里的「发起行为对比」，
          系统会自动补提取缺失的指纹并完成对比；新跑的任务会在 case 结束后自动生成指纹，之后对比更快。
        </div>
      </>}
    />
  );
}

export default function BehaviorCompareSection({
  jobId, compareJobId, refreshKey = 0, activeLabel, activeStrong, onFilter, openSceneId, onCloseScene,
}: Props) {
  const [summary, setSummary] = useState<BehaviorCompareSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  /** 没有报告时的指纹就绪情况（决定显示「可直接对比 / 缺指纹补算 / 探测失败」）。 */
  const [readiness, setReadiness] = useState<BehaviorReadiness | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  /** 已发起但还没有任何结果时（翻转场景还在算）：区块显示计算进度，而不是「还没有报告 / 去发起」。 */
  const [running, setRunning] = useState<BehaviorProgress | null>(null);

  // 取汇总
  useEffect(() => {
    let alive = true;
    setSummaryLoading(true);
    setSummaryError(null);
    setReadiness(null);
    setRunning(null);
    getBehaviorCompareSummary({ jobId, baseJobId: compareJobId })
      .then((s) => alive && setSummary(s))
      .catch((e) => {
        if (!alive) return;
        setSummary(null);
        setSummaryError(e instanceof Error ? e.message : '加载行为对比汇总失败');
        // 没有结果：先看是不是已经发起、正在算；否则查指纹就绪情况，告诉用户「差什么、要多久」
        getBehaviorProgress(jobId, compareJobId)
          .then((p) => alive && p && ['running', 'flipped_ready'].includes(p.status) && setRunning(p))
          .catch(() => undefined);
        getBehaviorReadiness(jobId, compareJobId)
          .then((r) => alive && setReadiness(r))
          .catch(() => alive && setReadiness(null));
      })
      .finally(() => alive && setSummaryLoading(false));
    return () => {
      alive = false;
    };
  }, [jobId, compareJobId, reloadKey, refreshKey]);

  // 抽屉要的是「这个场景的一行」：只用来显示标题与回放按钮，其余字段详情接口会自己取
  const [drawerRow, setDrawerRow] = useState<BehaviorSceneRow | null>(null);
  useEffect(() => {
    if (openSceneId == null || !summary) {
      setDrawerRow(null);
      return undefined;
    }
    let alive = true;
    getBehaviorCompareSceneDetail(summary.compareId, openSceneId)
      .then((d) => alive && setDrawerRow(d))
      .catch(() => alive && setDrawerRow(null));
    return () => {
      alive = false;
    };
  }, [openSceneId, summary]);

  return (
    <div className="bc-section-wrap">
      <div className="bc-section-header">
        <span className="bc-section-heading">行为对比</span>
        <span className="bc-section-sub">
          闭环段上逐场景比较自车怎么开
          {summary ? (
            <>
              ·共同场景 {summary.commonSceneNum.toLocaleString('en-US')}·仅一边有 {summary.onlyOneSideNum.toLocaleString('en-US')}
              ·噪声基准 {summary.noiseBaseline ?? '未标定'}·规则 {summary.ruleVersion}
            </>
          ) : null}
        </span>
        <Button size="small" icon={<ReloadOutlined />} onClick={() => setReloadKey((k) => k + 1)}>
          重新拉取
        </Button>
      </div>

      {summaryLoading ? <Spin style={{ display: 'block', margin: '24px auto' }} /> : null}
      {summaryError && !summaryLoading && running ? (
        <Alert
          type="info" showIcon
          message="行为对比计算中：正在先算两侧评估结论不同的场景"
          description={`已算完 ${running.computedSceneNum} / ${(running.flippedTotal ?? 0) + (running.otherTotal ?? 0)} 个场景。`
            + `${running.flippedTotal ? `其中两侧结论不同的有 ${running.flippedTotal} 个，会优先计算，算完这里先显示。` : '两侧结论完全相同，全部算完后这里显示结果。'}`}
        />
      ) : null}
      {summaryError && !summaryLoading && !running ? (
        /没有|不存在|未发起|无.*报告/.test(summaryError)
          ? <NoReportNotice readiness={readiness} reason={summaryError} />
          : (
            <Alert
              type="error" showIcon
              message="行为对比加载失败"
              // 网络/权限/5xx 不能伪装成「还没有报告」，否则会诱导用户重复发起
              description={summaryError}
              action={<Button size="small" onClick={() => setReloadKey((k) => k + 1)}>重试</Button>}
            />
          )
      ) : null}

      {summary && !summaryLoading ? (
        <>
          {summary.partial ? (
            <Alert
              type="warning" showIcon style={{ marginBottom: 10 }}
              message="部分结果：已先算完两侧评估结论不同的场景，其余场景仍在后台计算"
              description="此时按行为结论 / 强事件筛选，只会命中已算完的场景；全部算完后这里会自动更新。"
            />
          ) : summary.status !== 'succeeded' ? (
            <Alert type="info" showIcon style={{ marginBottom: 10 }} message={`对比状态：${summary.status}，数据可能不完整`} />
          ) : null}
          <OverviewCards
            summary={summary}
            activeLabel={activeLabel as BehaviorLabel | undefined}
            activeStrong={activeStrong}
            onLabel={(label?: BehaviorLabel) => onFilter({ label, strong: activeStrong })}
            onStrong={(strong) => onFilter({ label: activeLabel, strong })}
          />
          <div className="bc-muted" style={{ marginTop: 8 }}>
            点上面的卡片或强事件标签，会筛选下方「场景级对比明细」；与翻转象限、「差异」等筛选取交集。
            点明细里的「行为」标签可查看该场景详情。
          </div>
          <SceneDrawer compareId={summary.compareId} row={drawerRow} onClose={onCloseScene} />
        </>
      ) : null}
    </div>
  );
}
